/**
 * useProposals — out-of-band proposal tray hook.
 *
 * Manages the full lifecycle of proposals that arrive as DB rows rather
 * than inline SSE frames (Phase 1 background tasks, cross-session worker).
 *
 * Behaviour:
 *  - Fetches ALL pending proposals on mount (not session-scoped).
 *  - Re-fetches after each completed turn via `refreshAfterTurn()`.
 *  - Exposes optimistic accept/reject so the tray updates instantly.
 *  - Never throws: API failures are swallowed and surfaced as `error`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ProposalsApi } from "../../api/proposalsApi";
import type { Proposal } from "../../api/proposalsApi";

export interface UseProposalsResult {
  /** All currently pending proposals (optimistically updated). */
  proposals: Proposal[];
  /** True while the initial fetch is in flight. */
  loading: boolean;
  /** Last fetch error, if any. Cleared on the next successful fetch. */
  error: string | null;
  /** Number of pending proposals (badge count). */
  pendingCount: number;
  /** Accept a proposal. Optimistically removes it from the list;
   *  restores on failure and sets `error`. */
  accept: (proposal_id: string) => Promise<void>;
  /** Reject a proposal with an optional reason. Same optimistic behaviour. */
  reject: (proposal_id: string, reason?: string) => Promise<void>;
  /** Call this after a turn completes to pick up any newly created
   *  background proposals. */
  refreshAfterTurn: () => void;
}

export function useProposals(
  proposalsApi: ProposalsApi | null | undefined,
): UseProposalsResult {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether a fetch is already in flight to avoid double-fetches.
  const fetchingRef = useRef(false);

  const loadProposals = useCallback(
    async (initial = false) => {
      if (!proposalsApi || fetchingRef.current) return;
      fetchingRef.current = true;
      if (initial) setLoading(true);
      try {
        // Fetch all pending proposals (no session filter) so the tray
        // shows proposals from background tasks that ran after the turn.
        const rows = await proposalsApi.list();
        setProposals(rows);
        setError(null);
      } catch (exc) {
        setError(exc instanceof Error ? exc.message : "Failed to load proposals");
      } finally {
        fetchingRef.current = false;
        if (initial) setLoading(false);
      }
    },
    [proposalsApi],
  );

  // Initial fetch on mount (or when proposalsApi becomes available).
  useEffect(() => {
    loadProposals(true);
  }, [loadProposals]);

  const refreshAfterTurn = useCallback(() => {
    // Small delay so the background task has time to write its DB row
    // before we fetch. 1.5s is enough for the Haiku call + DB write.
    setTimeout(() => {
      void loadProposals();
    }, 1500);
  }, [loadProposals]);

  const accept = useCallback(
    async (proposal_id: string) => {
      if (!proposalsApi) return;
      // Optimistic: remove from list immediately.
      const previous = proposals;
      setProposals((prev) => prev.filter((p) => p.proposal_id !== proposal_id));
      try {
        await proposalsApi.accept(proposal_id);
      } catch (exc) {
        // Restore on failure.
        setProposals(previous);
        const msg = exc instanceof Error ? exc.message : "Failed to accept";
        setError(msg);
        throw exc;
      }
    },
    [proposalsApi, proposals],
  );

  const reject = useCallback(
    async (proposal_id: string, reason?: string) => {
      if (!proposalsApi) return;
      const previous = proposals;
      setProposals((prev) => prev.filter((p) => p.proposal_id !== proposal_id));
      try {
        await proposalsApi.reject(proposal_id, reason);
      } catch (exc) {
        setProposals(previous);
        const msg = exc instanceof Error ? exc.message : "Failed to reject";
        setError(msg);
        throw exc;
      }
    },
    [proposalsApi, proposals],
  );

  return {
    proposals,
    loading,
    error,
    pendingCount: proposals.length,
    accept,
    reject,
    refreshAfterTurn,
  };
}
